import { canFold } from '@/domain/product/canFold';
import { deriveCardForm } from '@/domain/product/deriveCardForm';
import { EProductCategory } from '@/domain/product/EProductCategory';
import type { EProductCardForm } from '@/domain/product/EProductCardForm';
import type { EProductFormat } from '@/domain/product/EProductFormat';
import type { IProduct } from '@/domain/product/IProduct';

// The loaded product, with the questions the app asks of it answered here rather than composed
// at each call site. Getters delegate to the rules that hold over values -- deriveCardForm and
// canFold stay standalone because pickProduct3DStrategy applies them to mesh params, not to a
// product.
class Product implements IProduct {
    readonly cuttingPath: string | null;
    readonly format: EProductFormat;
    readonly has3d: boolean;
    readonly height: number;
    readonly helpGridRecto: string | null;
    readonly helpGridVerso: string | null;
    readonly id: string;
    readonly isColorsLocked: boolean;
    readonly label: string;
    readonly meshBaseName: string | null;
    readonly model: string | null;
    readonly name: string;
    readonly width: number;

    get category(): EProductCategory {
        return this.has3d ? EProductCategory.Glass : EProductCategory.Card;
    }

    // A Glass never carries a cutting path, even when the backend returned one. Normalised here
    // so deriveCardForm stays total.
    get cuttingPathUuid(): string | null {
        return this.has3d ? null : this.cuttingPath;
    }

    get cardForm(): EProductCardForm | null {
        return deriveCardForm(this.category, this.cuttingPathUuid);
    }

    get canFold(): boolean {
        return canFold(this.cardForm);
    }

    constructor(data: IProduct) {
        this.cuttingPath = data.cuttingPath;
        this.format = data.format;
        this.has3d = data.has3d;
        this.height = data.height;
        this.helpGridRecto = data.helpGridRecto;
        this.helpGridVerso = data.helpGridVerso;
        this.id = data.id;
        this.isColorsLocked = data.isColorsLocked;
        this.label = data.label;
        this.meshBaseName = data.meshBaseName;
        this.model = data.model;
        this.name = data.name;
        this.width = data.width;
    }

    // Replaces the object spread the store used to do. Spreading an instance drops the
    // prototype, so every getter would come back undefined -- tsc catches it, but only because
    // the getters are declared; this method removes the question.
    with(patch: Partial<IProduct>): Product {
        return new Product({ ...this, ...patch });
    }
}

export { Product };
